import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import { useWebPush } from '../hooks/useWebPush';

/**
 * Post-login prompt that asks the user to enable lock-screen notifications.
 *
 * Without this, a user can flip the in-app "Direct messages" toggle and still
 * never receive an OS push because the browser was never actually subscribed
 * to the PushManager. `useWebPush().enable()` is what does the real work —
 * `Notification.requestPermission()` + `pushManager.subscribe()` + POST to
 * `/api/push/subscribe`.
 *
 * Fires when ALL of the following are true:
 *   1. `handleLogin` (or cold-launch session-restore) just ran
 *      (`sessionStorage.allure_notifications_prompt_pending === '1'`).
 *   2. The device supports Web Push (Notification + serviceWorker + PushManager).
 *   3. The user hasn't already granted permission AND subscribed.
 *   4. The user hasn't dismissed in the last COOLDOWN_DAYS days.
 *   5. The user hasn't permanently denied permission at the OS level.
 *
 * Silent auto-resubscribe path: if `Notification.permission === 'granted'`
 * but we have no `PushSubscription` (subscription got cleaned up after a
 * 404/410), we re-subscribe immediately without showing the modal — no UX
 * cost, no missing pushes.
 *
 * "Maybe later" sets a 30-day cooldown so we don't nag.
 */
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = '#703FF6';

const PENDING_KEY = 'allure_notifications_prompt_pending';
const DISMISSED_KEY = 'allure_notifications_prompt_dismissed_at';
const COOLDOWN_DAYS = 30;
const LANDING_DELAY_MS = 1800;

export const EnableNotificationsPrompt = ({ user }) => {
  const { isSupported, permission, isSubscribed, enable, syncExistingSubscription } = useWebPush();
  const [open, setOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const dismissForCooldown = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
  }, []);

  // Decide whether to show the prompt whenever `user` becomes available.
  useEffect(() => {
    let cancelled = false;
    const syncGrantedPermission = async () => {
      if (!user || !isSupported) return;
      if (window.Notification?.permission !== 'granted') return;
      try {
        const syncResult = await syncExistingSubscription();
        if (syncResult?.reason === 'no_subscription') {
          await enable();
        }
      } catch {
        /* syncExistingSubscription / enable already log */
      }
    };
    const decide = async () => {
      if (!user) return;
      if (!isSupported) return;

      // Critical repair path for Android TWA: users can enable notifications
      // manually in Android Settings, or a previous backend cleanup can remove
      // the stored endpoint while the browser still has a PushSubscription.
      // In both cases we must POST the existing subscription to our backend on
      // every authenticated launch — not only after the modal flag is set.
      if (permission === 'granted') {
        try {
          const syncResult = await syncExistingSubscription();
          if (syncResult?.reason === 'no_subscription') {
            await enable();
          }
        } catch {
          /* syncExistingSubscription / enable already log */
        }
        return;
      }

      let pending = false;
      try {
        pending = window.sessionStorage.getItem(PENDING_KEY) === '1';
      } catch {
        /* noop */
      }
      if (!pending) return;
      // Always clear the flag early so we never re-trigger on route changes.
      try { window.sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ }

      // Permission permanently denied at the OS level — modal can't help.
      if (permission === 'denied') return;

      // Already subscribed → nothing to do.
      if (isSubscribed) return;

      try {
        const dismissedAt = window.localStorage.getItem(DISMISSED_KEY);
        if (dismissedAt) {
          const ageDays = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
          if (Number.isFinite(ageDays) && ageDays < COOLDOWN_DAYS) return;
        }
      } catch {
        /* noop */
      }

      // Tiny delay so the user has visually arrived on their post-login screen
      // before a modal pops on top of the route transition.
      window.setTimeout(() => {
        if (!cancelled) setOpen(true);
      }, LANDING_DELAY_MS);
    };
    decide();
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        syncGrantedPermission();
      }
    };
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [user, isSupported, isSubscribed, permission, enable, syncExistingSubscription]);

  const handleEnable = async () => {
    if (enrolling) return;
    setEnrolling(true);
    try {
      const result = await enable();
      if (result?.ok) {
        toast.success('Notifications on — we’ll ping you when messages arrive.');
        setOpen(false);
      } else if (result?.reason === 'denied') {
        toast.message('No worries. You can turn this on later from Settings.');
        dismissForCooldown();
        setOpen(false);
      } else {
        toast.error('Could not enable notifications. Try again from Settings.');
        setOpen(false);
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleLater = () => {
    dismissForCooldown();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="enable-notifications-title"
      data-testid="enable-notifications-prompt"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10400,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(20, 12, 44, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: '20px',
        animation: 'allure-notif-fade 200ms ease-out',
      }}
    >
      <style>{`
        @keyframes allure-notif-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes allure-notif-slide { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#FFFFFF',
          borderRadius: 24,
          padding: '28px 22px 22px',
          boxShadow: '0 22px 60px rgba(28, 18, 64, 0.32)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          marginBottom: 'max(env(safe-area-inset-bottom), 24px)',
          animation: 'allure-notif-slide 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          position: 'relative',
        }}
      >
        <button
          onClick={handleLater}
          aria-label="Dismiss notifications prompt"
          data-testid="enable-notifications-prompt-close"
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: 'none',
            background: 'var(--accent-surface)',
            color: 'var(--text-2)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={16} strokeWidth={2.2} />
        </button>

        <div
          style={{
            alignSelf: 'center',
            width: 64,
            height: 64,
            borderRadius: 20,
            background: 'linear-gradient(135deg, rgba(112, 63, 246, 0.14), rgba(74, 32, 201, 0.18))',
            color: BRAND,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 4,
          }}
        >
          <BellRing size={32} strokeWidth={2} />
        </div>

        <div style={{ textAlign: 'center' }}>
          <h2
            id="enable-notifications-title"
            style={{
              margin: 0,
              fontFamily: FONT,
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--text)',
              lineHeight: 1.25,
            }}
          >
            Never miss a message
          </h2>
          <p
            style={{
              margin: '10px 0 0',
              fontFamily: SUB_FONT,
              fontSize: 14,
              color: 'var(--text-2)',
              lineHeight: 1.5,
            }}
          >
            Allow Allure to ping your phone when guests, hosts, venues or
            Support reach out — even when the app is closed.
          </p>
        </div>

        <button
          onClick={handleEnable}
          disabled={enrolling}
          data-testid="enable-notifications-prompt-allow"
          style={{
            width: '100%',
            padding: '15px 16px',
            borderRadius: 16,
            border: 'none',
            background: enrolling
              ? 'rgba(112, 63, 246, 0.5)'
              : 'linear-gradient(135deg, #703FF6 0%, #4A20C9 100%)',
            color: '#FFFFFF',
            fontFamily: SUB_FONT,
            fontSize: 15,
            fontWeight: 700,
            cursor: enrolling ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: '0 10px 24px rgba(112, 63, 246, 0.3)',
          }}
        >
          <MessageSquare size={18} strokeWidth={2.2} />
          {enrolling ? 'Setting up…' : 'Turn on notifications'}
        </button>

        <button
          onClick={handleLater}
          disabled={enrolling}
          data-testid="enable-notifications-prompt-later"
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 16,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-2)',
            fontFamily: SUB_FONT,
            fontSize: 14,
            fontWeight: 600,
            cursor: enrolling ? 'wait' : 'pointer',
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default EnableNotificationsPrompt;
